import { useEffect, useMemo, useState } from 'react';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import TextField from '@mui/material/TextField';
import Button from 'react-bootstrap/Button';
import Box from '@mui/material/Box';
import { useRestApi } from 'context/RestApiContext';

export default function TaskCardEditCheckList({
  cardId,
  checklist,
  onChange,
  suggestions = [],
  checkItemApiPath,
}) {
  const restApi = useRestApi();
  const [list, setList] = useState([]);
  const [newItem, setNewItem] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [hoveredId, setHoveredId] = useState(null);

  // API パス（未指定時はタスクカード用のデフォルト）
  const apiPath = checkItemApiPath || `/api/task/cards/${cardId}/check_item`;

  useEffect(() => {
    setList(Array.isArray(checklist) ? checklist : []);
  }, [checklist]);

  const taken = useMemo(
    () => new Set(list.map((it) => (it.text ?? '').trim().toLowerCase())),
    [list]
  );
  const filteredSuggestions = useMemo(
    () => (suggestions || []).filter((s) => !taken.has((s ?? '').trim().toLowerCase())),
    [suggestions, taken]
  );

  // サーバーへ送信
  const emit = (item) => {
    console.log('emit', item);
    if (item) {
      restApi.post(apiPath, item, (result) => {
        const next =
          item.CARD_ITEM_ID == null
            ? [...list, result]
            : list
                .map((it) => (it.CARD_ITEM_ID === item.CARD_ITEM_ID ? result : it))
                .filter((it) => it.DEL_FLG !== '1');
        setList(next);
        onChange(next);
      });
    }
  };

  // アイテムの追加
  const handleAdd = (textArg) => {
    const text = ((textArg ?? newItem) || '').trim();
    if (!text) return;
    emit({ CARD_ITEM_ID: null, WORK_CONTENT: text });
    setNewItem('');
  };

  // チェックの切り替え
  const handleToggle = (id) => {
    const target = list.find((it) => it.CARD_ITEM_ID === id);
    emit({ CARD_ITEM_ID: id, DONE_FLG: target.DONE_FLG ? 0 : 1 });
  };

  // テキストの変更
  const handleChangeText = (id) => (e) => {
    const v = e?.target?.value ?? '';
    // 入力中はローカル state のみ更新（emit しない）
    setList((prev) => prev.map((it) => (it.CARD_ITEM_ID === id ? { ...it, WORK_CONTENT: v } : it)));
  };
  // アイテムの削除
  const handleDelete = (id) => {
    console.log('delete', id);
    const target = list.find((it) => it.CARD_ITEM_ID === id);
    if (!target) return;
    emit({ CARD_ITEM_ID: target.CARD_ITEM_ID, DEL_FLG: '1' });
  };

  // 編集開始・終了
  const handleStartEdit = (id) => setEditingId(id);
  const handleCommitEdit = (id) => {
    setEditingId(null);
    const target = list.find((it) => it.CARD_ITEM_ID === id);
    if (!target) return;
    // 確定時のみ送信
    const updItem = { CARD_ITEM_ID: target.CARD_ITEM_ID, WORK_CONTENT: target.WORK_CONTENT };
    emit(updItem);
  };

  return (
    <div className="taskcard-edit-checklist">
      <List dense sx={{ m: 0, p: 0 }}>
        {list.map((it) => (
          <ListItem
            key={it.CARD_ITEM_ID}
            sx={{ bgcolor: 'inherit', borderRadius: 1, mb: 0, mt: 0, py: 0, minHeight: 'auto' }}
          >
            <Checkbox
              edge="start"
              checked={it.DONE_FLG === 1}
              onChange={() => handleToggle(it.CARD_ITEM_ID)}
              tabIndex={-1}
            />
            <Box sx={{ m: 0.5, flex: 1 }}>
              {editingId === it.CARD_ITEM_ID ? (
                <TextField
                  fullWidth
                  size="small"
                  value={it.text}
                  onChange={handleChangeText(it.CARD_ITEM_ID)}
                  onBlur={() => handleCommitEdit(it.CARD_ITEM_ID)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                      e.preventDefault();
                      handleCommitEdit(it.CARD_ITEM_ID);
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': { backgroundColor: '#fff' },
                    '& .MuiOutlinedInput-input': {
                      textDecoration: it.checked ? 'line-through' : 'none',
                      color: it.checked ? 'text.disabled' : 'inherit',
                    },
                  }}
                  autoFocus
                />
              ) : (
                <span
                  className="taskcard-item-text"
                  onClick={() => handleStartEdit(it.CARD_ITEM_ID)}
                  onMouseEnter={() => setHoveredId(it.CARD_ITEM_ID)}
                  onMouseLeave={() => setHoveredId(null)}
                  style={{
                    cursor: 'text',
                    textDecoration: it.DONE_FLG === 1 ? 'line-through' : 'none',
                    color: it.DONE_FLG === 1 ? 'rgba(0,0,0,0.38)' : 'inherit',
                    display: 'flex',
                    alignItems: 'center',
                    padding: '6px 6px',
                    width: '100%',
                    gap: '6px',
                  }}
                >
                  <span
                    style={{
                      flex: 1,
                      minWidth: 0,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {it.WORK_CONTENT || '（無題）'}
                  </span>
                  <IconButton
                    size="small"
                    aria-label="delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(it.CARD_ITEM_ID);
                    }}
                    sx={{
                      ml: 'auto', // 右寄せ
                      p: '2px',
                      visibility: hoveredId === it.CARD_ITEM_ID ? 'visible' : 'hidden',
                      pointerEvents: hoveredId === it.CARD_ITEM_ID ? 'auto' : 'none',
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              )}
            </Box>
          </ListItem>
        ))}
      </List>

      <div className="mt-1" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <TextField
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          placeholder="項目を入力して Enter または 追加 を押下"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
              e.preventDefault();
              handleAdd();
            }
          }}
          size="small"
          sx={{
            width: '100%',
            maxWidth: 560,
            '& .MuiOutlinedInput-root': { backgroundColor: '#fff' },
          }}
          inputProps={{ list: 'taskcard-edit-suggestions' }}
        />
        <datalist id="taskcard-edit-suggestions">
          {filteredSuggestions.map((s, idx) => (
            <option key={idx} value={s} />
          ))}
        </datalist>
        <Button
          style={{ height: '40px', paddingTop: '6px' }}
          disabled={!newItem.trim()}
          variant="primary"
          className="btn-fill"
          onClick={() => handleAdd()}
        >
          追加
        </Button>
      </div>
    </div>
  );
}
